import { useCallback, useMemo } from 'react';
import { ApolloError, NetworkStatus } from '@apollo/client';

import {
  useMarketingAiAnswerCardQuery,
  useMarketingAiVisibilityReportQuery,
  useMarketingSeoBudgetCardQuery,
  useMarketingSeoCompetitorsReportQuery,
  useMarketingSeoFunnelReportQuery,
  useMarketingSeoIdeasListQuery,
  useMarketingSeoKeywordCardQuery,
  useMarketingSeoKeywordsListQuery,
  useMarketingSeoLinksReportQuery,
  useMarketingSeoOpportunitiesListQuery,
  useMarketingSeoOverviewReportQuery,
  useMarketingSeoSeriesReportQuery,
  useMarketingSeoTaxonomyReportQuery,
  useMarketingSeoWeeksListQuery,
} from 'core/graphql';

import { marketingGqlFilter, marketingQueryErrorCode } from './marketingApi';
import { marketingSeoOpportunityFromRaw } from './marketingSeoModel';
import {
  IMarketingSeoAiAnswer,
  IMarketingSeoAiReport,
  IMarketingSeoBacklinks,
  IMarketingSeoBudget,
  IMarketingSeoCompetitors,
  IMarketingSeoFunnelRow,
  IMarketingSeoGapsPage,
  IMarketingSeoKeywordCard,
  IMarketingSeoKeywordsPage,
  IMarketingSeoOpportunity,
  IMarketingSeoOverview,
  IMarketingSeoPoint,
  IMarketingSeoTaxonomy,
  IMarketingSeoWeek,
} from './seoTypes';
import { IMarketingFilter } from './types';
import {
  IMarketingQueryOptions,
  IMarketingQueryState,
  MARKETING_NEXT_FETCH_POLICY,
} from './useMarketingReports';
import { useMarketingPermissions } from './useMarketingPermissions';

/**
 * ТЗ 0103, этап 4 — чтение SEO (DataForSEO). Те же правила, что у
 * `useMarketingReports`: `cache-and-network`, прошлый ответ, пока едет
 * новый (`previousData`), без права или на чужой подвкладке запрос не
 * уходит, код отказа — `errorCode`.
 *
 * Неделя снимка и сравнения — ISO-неделя адреса (`snap`, `csnap`) или
 * `null` (сервер берёт последнюю и предыдущую).
 */

interface IRawResult {
  loading: boolean;
  networkStatus: NetworkStatus;
  error?: ApolloError;
  refetch: () => Promise<unknown>;
}

const useSeoState = <T>(
  raw: IRawResult,
  current: T | null,
  skip: boolean,
): IMarketingQueryState<T> => {
  const { refetch: rawRefetch } = raw;
  const refetch = useCallback(() => {
    if (skip) return;
    rawRefetch().catch(() => undefined);
  }, [skip, rawRefetch]);
  return {
    data: current,
    loading: raw.loading && current === null,
    fetching: raw.loading && raw.networkStatus !== NetworkStatus.poll,
    error: raw.error,
    errorCode: marketingQueryErrorCode(raw.error),
    refetch,
  };
};

const useSkip = (enabled: boolean | undefined) => {
  const { canUse } = useMarketingPermissions();
  return !canUse || enabled === false;
};

/** Недельные снимки — выбор `snap` / `csnap` и «нет прошлого снимка». */
export const useMarketingSeoWeeks = ({
  enabled = true,
}: IMarketingQueryOptions = {}): IMarketingQueryState<IMarketingSeoWeek[]> => {
  const skip = useSkip(enabled);
  const result = useMarketingSeoWeeksListQuery({
    variables: { limit: 26 },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current =
    (result.data ?? result.previousData)?.marketingSeoWeeks ?? null;
  return useSeoState(result, current, skip);
};

export interface IMarketingSeoScope {
  /** ISO-неделя снимка (`2026-W41`); `null` — последняя. */
  week: string | null;
  /** ISO-неделя сравнения; `null` — предыдущая. */
  compareWeek: string | null;
  /** slug курса или `none`; `null` — все. */
  familySlug?: string | null;
  /** `true` — бренд, `false` — без бренда, `null` — все. */
  brand?: boolean | null;
}

/** SEO › «Обзор»: KPI, «Мы и конкуренты», движения, идеи недели. */
export const useMarketingSeoOverview = (
  scope: IMarketingSeoScope,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSeoOverview> => {
  const skip = useSkip(enabled);
  const result = useMarketingSeoOverviewReportQuery({
    variables: {
      week: scope.week,
      compareWeek: scope.compareWeek,
      familySlug: scope.familySlug ?? null,
      brand: scope.brand ?? null,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current =
    (result.data ?? result.previousData)?.marketingSeoOverview ?? null;
  return useSeoState(result, current, skip);
};

export interface IMarketingSeoKeywordsArgs extends IMarketingSeoScope {
  change?: string | null;
  zone?: string | null;
  domain?: string | null;
  search?: string | null;
  orderBy?: string | null;
  limit: number;
  offset?: number;
}

/** SEO › «Позиции»: страница запросов, числа чипов, зоны по неделям. */
export const useMarketingSeoKeywords = (
  args: IMarketingSeoKeywordsArgs,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSeoKeywordsPage> => {
  const skip = useSkip(enabled);
  const result = useMarketingSeoKeywordsListQuery({
    variables: {
      week: args.week,
      compareWeek: args.compareWeek,
      familySlug: args.familySlug ?? null,
      brand: args.brand ?? null,
      change: args.change ?? null,
      zone: args.zone ?? null,
      domain: args.domain ?? null,
      search: args.search?.trim() || null,
      orderBy: args.orderBy ?? null,
      limit: args.limit,
      offset: args.offset ?? 0,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current =
    (result.data ?? result.previousData)?.marketingSeoKeywords ?? null;
  return useSeoState(result, current, skip);
};

export interface IMarketingSeoKeywordState
  extends IMarketingQueryState<IMarketingSeoKeywordCard> {
  /** Запроса нет (`not_found`) — панель говорит это словами. */
  notFound: boolean;
}

/**
 * Панель запроса. Тело монтируется на каждый запрос заново — чужих
 * «прошлых данных» нет (`previousData` не берётся).
 */
export const useMarketingSeoKeyword = (
  id: string | null,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingSeoKeywordState => {
  const skip = useSkip(enabled) || !id;
  const result = useMarketingSeoKeywordCardQuery({
    variables: { id: id ?? '', weeks: 12 },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current = skip ? null : result.data?.marketingSeoKeyword ?? null;
  const state = useSeoState(result, current, skip);
  return { ...state, notFound: state.errorCode === 'not_found' };
};

/** «Видимость по неделям» — своя доля и доли доменов, зоны по неделям. */
export const useMarketingSeoSeries = (
  scope: Pick<IMarketingSeoScope, 'familySlug' | 'brand'> & {
    weeks?: number;
  },
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSeoPoint[]> => {
  const skip = useSkip(enabled);
  const result = useMarketingSeoSeriesReportQuery({
    variables: {
      familySlug: scope.familySlug ?? null,
      brand: scope.brand ?? null,
      weeks: scope.weeks ?? 26,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current =
    (result.data ?? result.previousData)?.marketingSeoSeries ?? null;
  return useSeoState(result, current, skip);
};

/**
 * SEO › «Конкуренты» и панель домена (`cacheFirst` — тот же ответ, что у
 * подвкладки, без второго запроса).
 */
export const useMarketingSeoCompetitors = (
  scope: Pick<IMarketingSeoScope, 'week' | 'familySlug'>,
  { enabled = true, cacheFirst = false }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSeoCompetitors> => {
  const skip = useSkip(enabled);
  const result = useMarketingSeoCompetitorsReportQuery({
    variables: {
      week: scope.week,
      familySlug: scope.familySlug ?? null,
    },
    skip,
    fetchPolicy: cacheFirst ? 'cache-first' : 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current =
    (result.data ?? result.previousData)?.marketingSeoCompetitors ?? null;
  return useSeoState(result, current, skip);
};

export interface IMarketingSeoGapsArgs {
  month?: string | null;
  familySlug?: string | null;
  unmapped?: boolean | null;
  kind?: string | null;
  /**
   * С навигационными запросами («gpt», «github») — только в строках; в
   * карточки и темы они не входят никогда.
   */
  includeNavigational?: boolean;
  limit: number;
  offset?: number;
}

/** SEO › «Что написать» и «Курсы» › «Спрос без курса». */
export const useMarketingSeoGaps = (
  args: IMarketingSeoGapsArgs,
  { enabled = true, cacheFirst = false }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSeoGapsPage> => {
  const skip = useSkip(enabled);
  const result = useMarketingSeoIdeasListQuery({
    variables: {
      month: args.month ?? null,
      familySlug: args.familySlug ?? null,
      unmapped: args.unmapped ?? null,
      kind: args.kind ?? null,
      // Ключ — только когда включено: запросы без него не делятся на «false»
      // и «нет аргумента» (тот же кеш, те же моки).
      ...(args.includeNavigational ? { includeNavigational: true } : {}),
      limit: args.limit,
      offset: args.offset ?? 0,
    },
    skip,
    fetchPolicy: cacheFirst ? 'cache-first' : 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current =
    (result.data ?? result.previousData)?.marketingSeoGaps ?? null;
  return useSeoState(result, current, skip);
};

export interface IMarketingSeoAiArgs {
  week: string | null;
  familySlug?: string | null;
  /** chatgpt | gemini | aio; `null` — ChatGPT и Gemini. */
  system?: string | null;
  /** neutral | comparison | brand; `null` — нейтральные. */
  group?: string | null;
}

/** SEO › «AI-ответы»: доли, домены, недели, вопросы; AI Overview. */
export const useMarketingSeoAi = (
  args: IMarketingSeoAiArgs,
  { enabled = true, cacheFirst = false }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSeoAiReport> => {
  const skip = useSkip(enabled);
  const result = useMarketingAiVisibilityReportQuery({
    variables: {
      week: args.week,
      familySlug: args.familySlug ?? null,
      system: args.system ?? null,
      group: args.group ?? null,
    },
    skip,
    fetchPolicy: cacheFirst ? 'cache-first' : 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current = (result.data ?? result.previousData)?.marketingSeoAi ?? null;
  return useSeoState(result, current, skip);
};

/** Панель вопроса AI: ответы недели по системам (тело — своё на вопрос). */
export const useMarketingSeoAiAnswers = (
  questionId: string | null,
  week: string | null,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSeoAiAnswer[]> & { notFound: boolean } => {
  const skip = useSkip(enabled) || !questionId;
  const result = useMarketingAiAnswerCardQuery({
    variables: { questionId: questionId ?? '', week },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current = skip ? null : result.data?.marketingSeoAiAnswers ?? null;
  const state = useSeoState(result, current, skip);
  return { ...state, notFound: state.errorCode === 'not_found' };
};

/** SEO › «Ссылки». */
export const useMarketingSeoBacklinks = (
  month: string | null,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSeoBacklinks> => {
  const skip = useSkip(enabled);
  const result = useMarketingSeoLinksReportQuery({
    variables: { month, limit: 100 },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current =
    (result.data ?? result.previousData)?.marketingSeoBacklinks ?? null;
  return useSeoState(result, current, skip);
};

/**
 * SEO › «Спрос» › «От поиска до продажи»: по курсу — доля видимости, Labs,
 * клики GSC, входы из органики, CRM-лиды и продажи seo_google. `null` у
 * чисел — источник не собран (≠ 0).
 */
export const useMarketingSeoFunnel = (
  filter: IMarketingFilter,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSeoFunnelRow[]> => {
  const skip = useSkip(enabled);
  const result = useMarketingSeoFunnelReportQuery({
    variables: { filter: marketingGqlFilter(filter) },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current =
    (result.data ?? result.previousData)?.marketingSeoFunnel ?? null;
  return useSeoState(result, current, skip);
};

/** «Данные» › бюджет DataForSEO (сервер его не кеширует). */
export const useMarketingSeoBudget = ({
  enabled = true,
}: IMarketingQueryOptions = {}): IMarketingQueryState<IMarketingSeoBudget> => {
  const skip = useSkip(enabled);
  const result = useMarketingSeoBudgetCardQuery({
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current =
    (result.data ?? result.previousData)?.marketingSeoBudget ?? null;
  return useSeoState(result, current, skip);
};

/** Возможности SEO (открытые и в плане); JSON разобран один раз на ответ. */
export const useMarketingSeoOpportunities = (
  args: {
    kinds?: string[] | null;
    status?: string[] | null;
    familySlug?: string | null;
    limit?: number;
  },
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingQueryState<IMarketingSeoOpportunity[]> => {
  const skip = useSkip(enabled);
  const result = useMarketingSeoOpportunitiesListQuery({
    variables: {
      kinds: args.kinds?.length ? args.kinds : null,
      status: args.status?.length ? args.status : null,
      familySlug: args.familySlug ?? null,
      limit: args.limit ?? 50,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const raw =
    (result.data ?? result.previousData)?.marketingSeoOpportunities ?? null;
  const current = useMemo(
    () => (raw ? raw.map(marketingSeoOpportunityFromRaw) : null),
    [raw],
  );
  return useSeoState(result, current, skip);
};

/** Справочник SEO для настроек: домены, вопросы AI, рынок DataForSEO. */
export const useMarketingSeoTaxonomy = ({
  enabled = true,
}: IMarketingQueryOptions = {}): IMarketingQueryState<IMarketingSeoTaxonomy> => {
  const skip = useSkip(enabled);
  const result = useMarketingSeoTaxonomyReportQuery({
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: MARKETING_NEXT_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });
  const current: IMarketingSeoTaxonomy | null =
    (result.data ?? result.previousData)?.marketingTaxonomy ?? null;
  return useSeoState(result, current, skip);
};
